import React from 'react';
import { Text } from '@/components/ui/typography';
import { cn } from '@/lib/utils';

const sectionDescriptions = {
  'Technical Screening': 'Score from initial screening test',
  'Non Technical': 'Score from personality and behavioral assessment',
  Coding: 'Score from coding evaluation',
};

const CustomPieChart = ({ scores }) => {
  let difficulty='';
  // If no scores, show nothing
  if (!scores) return <div>No score data available.</div>;

  // Prepare the sections for the pie chart
  const sections = [
    {
      label: 'Technical Screening',
      color: '#3f51b5',
      icon: (
        <img 
          src="/technical-support.png" 
          alt="coding" 
          style={{ width: 25, height: 25 }} 
        />
      ),
      value: typeof scores.screening_score === 'number' ? scores.screening_score : 0,
    },
    {
      label: 'Non Technical',
      color: '#ffc107',
      icon: (
        <img 
          src="/mind-map.png" 
          alt="coding" 
          style={{ width: 25, height: 25 }} 
        />
      ),
      value:
        typeof scores.personality_score === 'number' && typeof scores.behavioral_score === 'number'
          ? (scores.personality_score + scores.behavioral_score) / 2
          : typeof scores.personality_score === 'number'
          ? scores.personality_score
          : typeof scores.behavioral_score === 'number'
          ? scores.behavioral_score
          : 0,
    },
    {
      label: 'Coding',
      color: '#4caf50',
      icon: (
          <img 
            src="/code.png" 
            alt="coding" 
            style={{ width: 25, height: 25 }} 
          />
      ),
      value: typeof scores.coding_score === 'number' ? scores.coding_score : 0,
    },
    // {
    //   label: 'Communication',
    //   color: '#9c27b0',
    //   icon: '💻',
    //   value: typeof scores.communication_score === 'number' ? scores.communication_score : 0,
    // }
  ];

  // Only use sections with a positive value for the pie chart
  const numericSections = sections.filter(s => typeof s.value === 'number' && s.value > 0);
  const total = numericSections.reduce((sum, sec) => sum + sec.value, 0);

  // If no valid scores, show a message
  if (numericSections.length === 0 || total === 0) {
    return <div>No score data available for this candidate.</div>;
  }

  // Pie chart rendering
  let cumulativeAngle = 0;
  const radius = 100;

  return (
    <div className="w-full max-w-4xl mx-auto p-6">
      <div className="flex flex-col md:flex-row gap-8 items-center">
      <div className="flex flex-col gap-4">
        {sections.map((s, index) => (
          <div className="flex items-start gap-4 p-4 border border-border rounded-lg hover:bg-accent transition-colors" key={index}>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: s.color }}>{s.icon}</div>
            <div className="flex-1">
              <Text className="font-semibold text-lg">{s.label} {s.value || 0}%</Text>
              
              {/* Inline conditional rendering of difficulty */}
              {s.label.trim() === 'Technical Screening' && scores.screening_difficulty && (
                <>
                  <br />
                  <Text className="text-sm text-muted-foreground">
                    (Difficulty - {scores.screening_difficulty})
                  </Text>
                </>
              )}
              {s.label.trim() === 'Coding' && scores.coding_language && (
                <>
                  <br />
                  <Text className="text-sm text-muted-foreground">
                    (Language - {scores.coding_language})
                  </Text>
                </>
              )}
            </div>
            <div className="hidden group-hover:block text-xs text-muted-foreground">{sectionDescriptions[s.label]}</div>
          </div>
        ))}
      </div>

        <svg viewBox="0 0 320 320" className="ai-svg-chart" width={300} height={320}>
          <g transform="translate(160,160)">
            {numericSections.map((s, i) => {
              const value = s.value;
              const angle = (value / total) * 360;
              const startAngle = cumulativeAngle;
              const endAngle = cumulativeAngle + angle;
              cumulativeAngle += angle;

              // Convert angles to radians
              const startRadians = (Math.PI / 180) * startAngle;
              const endRadians = (Math.PI / 180) * endAngle;

              // Calculate coordinates
              const x1 = radius * Math.cos(startRadians);
              const y1 = radius * Math.sin(startRadians);
              const x2 = radius * Math.cos(endRadians);
              const y2 = radius * Math.sin(endRadians);

              // Large arc flag
              const largeArc = angle > 180 ? 1 : 0;

              // Gradient id for each slice
              const gradId = `pieGrad${i}`;

              return (
                <g key={i}>
                  <defs>
                    <linearGradient id={gradId} x1="0" y1="0" x2={x2} y2={y2}>
                      <stop offset="0%" stopColor={s.color} stopOpacity="0.85" />
                      <stop offset="100%" stopColor={s.color} stopOpacity="1" />
                    </linearGradient>
                  </defs>
                  <path
                    d={`M0,0 L${x1},${y1} A${radius},${radius} 0 ${largeArc} 1 ${x2},${y2} Z`}
                    fill={`url(#${gradId})`}
                    stroke="#fff"
                    strokeWidth="4"
                    filter="url(#pieShadow)"
                  />
                </g>
              );
            })}
            {/* Donut hole */}
            <circle r="60" fill="#fff" />
            {/* Center label */}
            {/* <text
              x="0"
              y="0"
              textAnchor="middle"
              dominantBaseline="middle"
              className="center-label"
            >
              {total}%
            </text> */}
            {/* Drop shadow filter */}
            <defs>
              <filter id="pieShadow" x="-30%" y="-30%" width="160%" height="160%">
                <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#000" floodOpacity="0.12" />
              </filter>
            </defs>
          </g>
        </svg>
      </div>
    </div>
  );
};

export default CustomPieChart;
